import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueService, SeasonService, LeagueSeasonService } from '../../services/firebaseService';
import { getUserTeamsForLeague } from '../../utils/teamUtils';
import SeasonTeamsList from '../../components/shared/SeasonTeamsList';

export default function PlayerStandings() {
  const { leagueId, seasonId } = useParams();
  const { userProfile } = useAuth();
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [leagueSeason, setLeagueSeason] = useState(null);
  const [userTeamIds, setUserTeamIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId && seasonId && userProfile) {
      loadStandingsData();
    }
  }, [leagueId, seasonId, userProfile]);

  const loadStandingsData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load league, season, and league-season data in parallel
      const [leagueData, seasonData, leagueSeasonData] = await Promise.all([
        LeagueService.getLeague(leagueId),
        SeasonService.getSeason(seasonId),
        LeagueSeasonService.getLeagueSeason(leagueId, seasonId)
      ]);

      if (!leagueData || !seasonData || !leagueSeasonData) {
        throw new Error('League, season, or league-season not found');
      }

      setLeague(leagueData);
      setSeason(seasonData);
      setLeagueSeason(leagueSeasonData);

      // Get user's teams in this league
      const userTeamsInLeague = getUserTeamsForLeague(userProfile.teams || {}, leagueId);
      const teamIds = userTeamsInLeague
        .filter(ut => ut.seasonId === seasonId)
        .map(ut => ut.teamId);
      setUserTeamIds(teamIds);

    } catch (err) {
      console.error('Error loading standings data:', err);
      setError(err.message || 'Failed to load standings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading standings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <TrophyIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Standings</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/"
            className="text-survivor-600 hover:text-survivor-700"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!league || !season) {
    return null;
  }

  // Combine league-season data with full season data
  const combinedSeason = {
    ...leagueSeason,
    ...season
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Homepage
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">League Standings</h1>
            <p className="mt-1 text-lg text-gray-600">{league.name} - {season.name || season.id}</p>
          </div>
        </div>

        {/* User's Teams Summary */}
        {userTeamIds.length > 0 && (
          <div className="bg-survivor-50 border border-survivor-200 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-survivor-900 mb-2">Your Teams</h2>
            <div className="flex flex-wrap gap-2">
              {userTeamIds.map(teamId => (
                <Link
                  key={teamId}
                  to={`/player/teams/${teamId}`}
                  className="px-3 py-1 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 text-sm font-medium"
                >
                  View Team #{teamId.substring(0, 8)}...
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Teams Leaderboard */}
        <div className="mb-6">
          <SeasonTeamsList
            leagueId={leagueId}
            seasonId={seasonId}
            season={combinedSeason}
            league={league}
            hideMembers={true}
            hideSubmissionStatus={true}
            showTeamLinks={true}
            hideFilters={true}
          />
        </div>
      </div>
    </div>
  );
}


